import { NavService } from '@thoughtspot/blink-context/cjs/interfaces';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { Checkbox } from '@thoughtspot/radiant-react/widgets/checkbox';
import { FormControl } from '@thoughtspot/radiant-react/widgets/form-control';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import {
    Horizontal,
    Vertical,
} from '@thoughtspot/radiant-react/widgets/layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import { SessionService } from '@thoughtspot/session-service';
import React, { useState } from 'react';
import { useCreatePinboardByNameMutation } from '/@services/get-pinboard-list/get-pinboard-list-service';
import styles from './create-liveboard-modal.module.scss';

export interface CreateLiveboardModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (state: boolean) => void;
    navService: NavService;
    sessionService: SessionService;
}

export const CreateLiveboardModal: React.FC<CreateLiveboardModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    navService,
    sessionService,
}) => {
    const {
        _createPinboard,
        loading: createPinboardLoading,
    } = useCreatePinboardByNameMutation();
    const [inputName, setInputName] = useState('');
    const [isNameValid, setIsNameValid] = useState(true);
    const [inputDescription, setInputDescription] = useState('');
    const onDismiss = () => {
        setInputName('');
        setInputDescription('');
        setIsNameValid(true);
        setIsModalOpen(false);
    };
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsNameValid(true);
        setInputName(event.target.value);
    };
    const handleTextAreaDescriptionChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        setInputDescription(event.target.value);
    };
    const isDiscoverabilityCheckBoxUnselected = sessionService.isDiscoverabilityCheckboxUnselectedPerOrg();
    const isDiscoverabilityCheckboxUnselectedPerOrg =
        isDiscoverabilityCheckBoxUnselected?.[sessionService.getCurrentOrgId()];
    const [checked, setChecked] = useState(
        isDiscoverabilityCheckboxUnselectedPerOrg === undefined
            ? !sessionService.isDiscoverabilityCheckboxUnselectedNonOrg()
            : !isDiscoverabilityCheckboxUnselectedPerOrg,
    );
    const handleChange = () => {
        setChecked(!checked);
    };
    const { t } = useTranslation();
    const logger = create('create-liveboard-redesigned-homepage');
    const modalHeader = t('homepage.redesign.create.header');

    return (
        <Modal
            title={modalHeader}
            isOpen={isModalOpen}
            size={ModalSize.SMALL}
            data-testid="create-liveboard-modal"
            confirmButton={{
                text: t('homepage.redesign.button'),
                isLoading: createPinboardLoading,
                onConfirm: async () => {
                    const trimmedName = inputName?.trim();
                    if (!trimmedName) {
                        setIsNameValid(false);
                        return;
                    }
                    if (!createPinboardLoading) {
                        await _createPinboard({
                            variables: {
                                name: trimmedName,
                                description: inputDescription,
                                ...(sessionService.isDiscoverabilityEnabled() && {
                                    discoverable: checked,
                                }),
                            },
                        })
                            .then(async res => {
                                const newPinboard =
                                    res.data.Pinboard__createByName;
                                onDismiss();
                                navService.goToPinboard(newPinboard.id);
                                return true;
                            })
                            .catch(err => {
                                logger.error(err);
                            });
                    }
                },
            }}
            dismissButton={{
                text: t('homeleftpanel.modal.cancel'),
            }}
            onDismiss={onDismiss}
        >
            <Vertical
                grow
                data-testid="create-liveboard-modal"
                className={styles.contentWrapper}
            >
                <FormControl
                    label={t('homepage.redesign.create.modal.liveboard.name')}
                    hasError={!isNameValid}
                    errorMessage={t('answerTitleDescription.emptyTitleError')}
                    className={styles.formControl}
                >
                    <Input.Text
                        autoFocus
                        ariaLabel={inputName}
                        value={inputName}
                        data-testid="create-pinboard-input"
                        aria-required
                        onChange={handleOnChange}
                        className={styles.name}
                    />
                </FormControl>
                <FormControl
                    label={t(
                        'homepage.redesign.create.modal.liveboard.description',
                    )}
                >
                    <TextArea
                        ariaLabel={inputDescription}
                        value={inputDescription}
                        name="textarea"
                        onChange={handleTextAreaDescriptionChange}
                        className={styles.name}
                        placeholder={t(
                            'homepage.redesign.create.modal.description.placeholder',
                        )}
                    />
                </FormControl>
                {sessionService.isDiscoverabilityEnabled() && (
                    <Horizontal hAlignContent="left" spacing="b">
                        <Checkbox
                            id="disc"
                            data-testid="create-pinboard-checkbox"
                            label={t('homepage.redesign.create.modal.checkbox')}
                            checked={checked}
                            onChange={handleChange}
                            className={styles.name}
                        />
                    </Horizontal>
                )}
            </Vertical>
        </Modal>
    );
};
